import type { ThemeChoice } from './theme-choice';

const KEY = 'kit.theme';

class ThemeStore {
  private readonly listeners = new Set<() => void>();
  private readonly media = window.matchMedia('(prefers-color-scheme: dark)');

  read = (): ThemeChoice => {
    const stored = localStorage.getItem(KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  };

  resolved(): 'light' | 'dark' {
    const choice = this.read();
    if (choice !== 'system') return choice;
    return this.media.matches ? 'dark' : 'light';
  }

  apply(): void {
    const resolved = this.resolved();
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolved === 'dark' ? '#18201b' : '#f7f6f2');
  }

  set(choice: ThemeChoice): void {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
    this.apply();
    for (const listener of this.listeners) listener();
  }

  subscribe = (listener: () => void): (() => void) => {
    const onChange = () => {
      this.apply();
      listener();
    };
    this.listeners.add(listener);
    this.media.addEventListener('change', onChange);
    return () => {
      this.listeners.delete(listener);
      this.media.removeEventListener('change', onChange);
    };
  };
}

export const themeStore = new ThemeStore();
